import { useState } from 'react'
import { PLANS, ALL_FEATURES, planHasFeature } from '../../Plan'
import { useDispatch, useSelector } from 'react-redux'
import './pricing.css'

const Check = ({ color = '#a8295d' }) => (
  <svg viewBox='0 0 10 10' fill='none'>
    <path
      d='M1.5 5L4 7.5L8.5 2.5'
      stroke={color}
      strokeWidth='1.7'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

function getTranslationText(keyPath, data = {}, lang = 'EN') {
  const validLangs = ['EN', 'FR', 'NL', 'SW']
  const selected = validLangs.includes(String(lang || 'EN').toUpperCase())
    ? String(lang || 'EN').toUpperCase()
    : 'EN'

  const keys = keyPath.split('.')
  let result = data
  for (const key of keys) {
    result = result?.[key]
    if (Array.isArray(result)) result = result[0]
  }

  if (result && typeof result === 'object') {
    return result[selected] ?? result.EN ?? 'Text not found'
  }

  return result || 'Text not found'
}

function PlanCard({ plan, onSelect }) {
  const dispatch = useDispatch()
  const agentData = useSelector((state) => state.agentData)
  const language = useSelector((state) => state.language)
  const translations = useSelector((state) => state.translationsData) || {}

  const currentPlanId = (agentData?.plan ?? 'root').toLowerCase().trim()
  const isCurrent = plan.id === currentPlanId
  const isFeatured = plan.id === 'care'
  const isFree = plan.price === 0

  const t = (keyPath, fallback = '') =>
    getTranslationText(keyPath, translations, language) || fallback

  const translatedName = t(plan.nameKey, plan.id)
  const translatedTier = t(plan.tierKey, '')
  const translatedDescription = t(plan.descriptionKey, '')
  const translatedBadge = t(plan.badgeKey, '') || null
  const translatedCta = t(plan.ctaKey, '')

  const ctaClass = isCurrent
    ? 'current'
    : plan.id === 'pro'
      ? 'upgrade'
      : 'select'

  const handleSelect = () => {
    if (isCurrent) return
    dispatch({ type: 'UPGRADE_PLAN', payload: plan.id })
    onSelect?.(plan)
  }

  return (
    <div className={`pp-card${isFeatured ? ' featured' : ''}`}>
      <div className='pp-card-accent' />
      <div className='pp-card-head'>
        <div>
          <div className='pp-plan-name'>{translatedName}</div>
          <div className='pp-plan-tier'>{translatedTier}</div>
        </div>
        {translatedBadge && (
          <div
            className={`pp-badge ${translatedBadge === 'Popular' || translatedBadge === 'Populaire' ? 'popular' : 'best'}`}
          >
            {translatedBadge}
          </div>
        )}
      </div>

      <div className='pp-pricing'>
        {isFree ? (
          <span className='pp-price-free'>{t('pricingPage.free', 'Free')}</span>
        ) : (
          <>
            <span className='pp-price-currency'>{plan.currency}</span>
            <span className='pp-price-amount'>{plan.price.toFixed(2)}</span>
            <span className='pp-price-period'>/ {plan.period}</span>
          </>
        )}
      </div>

      <div className='pp-desc'>{translatedDescription}</div>

      <div className='pp-features'>
        {plan.featureIds.map((featureId) => {
          const feature = ALL_FEATURES.find((item) => item.id === featureId)
          const label = feature ? t(feature.labelKey, feature.id) : featureId
          return (
            <div className='pp-feature-row' key={featureId}>
              <div className='pp-check-icon'>
                <Check />
              </div>
              {label}
            </div>
          )
        })}
      </div>

      <div className='pp-cta'>
        <button
          className={`pp-cta-btn ${ctaClass}`}
          onClick={handleSelect}
          disabled={isCurrent}
        >
          {translatedCta}
        </button>
      </div>
    </div>
  )
}

export default function PricingPage({
  currentPlanId = 'root',
  onBack,
  onSelectPlan,
}) {
  const [billing, setBilling] = useState('monthly')
  const language = useSelector((state) => state.language)
  const translations = useSelector((state) => state.translationsData) || {}

  const getText = (keyPath, fallback = '') =>
    getTranslationText(keyPath, translations, language) || fallback

  const handleSelect = (plan) => {
    onSelectPlan?.(plan)
  }

  return (
    <div className='pp-root'>
      <div className='pp-header'>
        {onBack && (
          <button className='pp-back' onClick={onBack}>
            ←
          </button>
        )}
        <span className='pp-header-title'>
          {getText('pricingPage.headerTitle', 'Pricing')}
        </span>
      </div>

      <div className='pp-hero'>
        <div className='pp-hero-eyebrow'>
          <span className='pp-hero-dot' />
          {getText('pricingPage.heroEyebrow', 'Halisi Plans')}
        </div>
        <div className='pp-hero-title'>
          {getText('pricingPage.heroTitleLine1', 'Choose your')}
          <br />
          {getText('pricingPage.heroTitleLine2', 'hair journey')}
        </div>
        <div className='pp-hero-sub'>
          {getText(
            'pricingPage.heroSub',
            'Every plan includes product recommendations personalised to your unique hair profile.',
          )}
        </div>
      </div>

      <div className='pp-toggle-wrap'>
        <div className='pp-toggle'>
          <button
            className={`pp-toggle-btn${billing === 'monthly' ? ' active' : ''}`}
            onClick={() => setBilling('monthly')}
          >
            {getText('pricingPage.billing.monthly', 'Monthly')}
          </button>
          <button
            className={`pp-toggle-btn${billing === 'annual' ? ' active' : ''}`}
            onClick={() => setBilling('annual')}
          >
            {getText('pricingPage.billing.annual', 'Annual')}{' '}
            <span className='pp-save-pill'>
              {getText('pricingPage.billing.save', 'Save 20%')}
            </span>
          </button>
        </div>
      </div>

      <div className='pp-cards'>
        {PLANS.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            currentPlanId={currentPlanId}
            onSelect={handleSelect}
          />
        ))}
      </div>

      <div className='pp-compare-wrap'>
        <div className='pp-compare-label'>
          {getText('pricingPage.comparison', 'Full feature comparison')}
        </div>
        <div className='pp-compare-card'>
          <div className='pp-compare-head'>
            <div className='pp-compare-head-blank' />
            {PLANS.map((p) => (
              <div
                key={p.id}
                className={`pp-compare-plan-name${p.id === 'care' ? ' featured' : ''}`}
              >
                {getText(p.nameKey, p.id)}
              </div>
            ))}
          </div>

          {ALL_FEATURES.map((feat) => (
            <div className='pp-compare-row' key={feat.id}>
              <div className='pp-compare-feat'>
                {getText(feat.labelKey, feat.id)}
              </div>
              {PLANS.map((p) => (
                <div className='pp-compare-cell' key={p.id}>
                  {planHasFeature(p.id, feat.id) ? (
                    <div className='pp-tick'>
                      <Check />
                    </div>
                  ) : (
                    <div className='pp-dash' />
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className='pp-footer-note'>
        {getText(
          'pricingPage.footerNote',
          'No contracts. Cancel anytime. Questions?',
        )}{' '}
        <a href='https://neotex.ai/' target='_blank' rel='noopener noreferrer'>
          {getText('pricingPage.contactUs', 'Contact us')}
        </a>
      </div>
    </div>
  )
}
